import type { TerminalVista } from '@barbones/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { CreditCard, Link2, Unplug } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, ErrorApi, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Insignia, Tarjeta, TarjetaEncabezado, Vacio } from '@/components/ui/data';
import { Input, Select, Switch } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { avisarError } from '@/lib/avisos';
import { esModuloNoConfigurado, mensajeParaCodigo, urlAutorizacionSegura, useCuentaMp, useTerminalesMp } from './mercadopago-datos';

const fechaLarga = (iso: string) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });

function FilaTerminal({ t, sucursalId }: { t: TerminalVista; sucursalId: string }) {
  const qc = useQueryClient();
  const [alias, setAlias] = useState(t.alias ?? '');
  useEffect(() => setAlias(t.alias ?? ''), [t.alias]);
  const refrescar = () => qc.invalidateQueries({ queryKey: ['mp-terminales', sucursalId] });

  const vincular = useMutation({
    mutationFn: () =>
      api(`/mercadopago/sucursales/${sucursalId}/terminales`, { method: 'POST', body: { terminalId: t.terminalId, alias: alias.trim() } }),
    onSuccess: () => {
      toast.success('Terminal vinculada');
      void refrescar();
    },
    onError: avisarError,
  });
  const actualizar = useMutation({
    mutationFn: (body: { alias?: string; activa?: boolean }) => api(`/mercadopago/terminales/${t.id}`, { method: 'PATCH', body }),
    onSuccess: () => void refrescar(),
    onError: avisarError,
  });

  const aliasLimpio = alias.trim();
  return (
    <li className="flex flex-col gap-3 border-b border-border/70 px-4 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="cifras truncate text-[13px] text-foreground-muted">{t.terminalId}</p>
        <div className="mt-1 flex items-center gap-2">
          {t.vinculada ? (
            <Insignia tono={t.enUso ? 'aviso' : 'exito'}>{t.enUso ? 'En uso' : 'Libre'}</Insignia>
          ) : (
            <Insignia tono="neutro">Disponible</Insignia>
          )}
          {t.vinculada && !t.activa && <Insignia tono="neutro">Desactivada</Insignia>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={alias}
          onChange={(e) => setAlias(e.target.value)}
          maxLength={40}
          placeholder="Alias (ej. Caja 1)"
          aria-label={`Alias de ${t.terminalId}`}
          className="w-full sm:w-48"
        />
        {t.vinculada ? (
          <>
            <Button
              variante="secundario"
              tamano="sm"
              onClick={() => actualizar.mutate({ alias: aliasLimpio })}
              disabled={!aliasLimpio || aliasLimpio === (t.alias ?? '')}
              cargando={actualizar.isPending}
              aria-label={`Guardar alias de ${t.terminalId}`}
            >
              Guardar
            </Button>
            <label className="flex items-center gap-2 text-[13px]">
              <Switch
                checked={t.activa}
                disabled={actualizar.isPending}
                onCheckedChange={(activa) => actualizar.mutate({ activa })}
                aria-label={`Activar ${t.terminalId}`}
              />
              Activa
            </label>
          </>
        ) : (
          <Button
            tamano="sm"
            onClick={() => vincular.mutate()}
            disabled={!aliasLimpio}
            cargando={vincular.isPending}
            aria-label={`Vincular ${t.terminalId}`}
          >
            <Link2 /> Vincular
          </Button>
        )}
      </div>
    </li>
  );
}

export function MercadoPagoPage() {
  const { sucursalId: sesionSucursal, usuario } = useSesion();
  const qc = useQueryClient();
  const busqueda = useSearch({ strict: false }) as { conectado?: unknown; error?: unknown };
  const navegar = useNavigate();
  const [elegida, setElegida] = useState<string | null>(null);
  const [apagado, setApagado] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const sucursales = useQuery({ queryKey: ['sucursales'], queryFn: () => api<{ id: string; nombre: string }[]>('/sucursales') });
  const permitidas = (sucursales.data ?? []).filter((s) => usuario?.sucursalIds.includes(s.id));
  const sucursalId = elegida ?? sesionSucursal ?? permitidas[0]?.id ?? null;
  const cuenta = useCuentaMp(sucursalId);
  const noConfigurado = apagado || esModuloNoConfigurado(cuenta.error);
  const conectada = cuenta.data?.conectada === true && cuenta.data.estado === 'activa';
  const terminales = useTerminalesMp(sucursalId, conectada);

  // Mensaje del regreso de Mercado Pago: una sola vez y se limpia la URL.
  const avisado = useRef(false);
  useEffect(() => {
    if (avisado.current) return;
    const ok = String(busqueda.conectado ?? '') === '1';
    const hayError = busqueda.error !== undefined;
    if (!ok && !hayError) return;
    avisado.current = true;
    if (hayError) toast.error(mensajeParaCodigo(busqueda.error));
    else toast.success('Mercado Pago conectado correctamente');
    void qc.invalidateQueries({ queryKey: ['mp-cuenta'] });
    void navegar({ to: '/configuracion/mercadopago', search: {}, replace: true });
  }, [busqueda.conectado, busqueda.error, navegar, qc]);

  const conectar = useMutation({
    mutationFn: () => api<{ url: string }>('/mercadopago/conexiones', { method: 'POST', body: { sucursalId } }),
    onSuccess: ({ url }) => {
      const segura = urlAutorizacionSegura(url);
      if (!segura) return void toast.error('La dirección de autorización recibida no es válida.');
      window.location.href = segura;
    },
    onError: (e) => (esModuloNoConfigurado(e) ? setApagado(true) : avisarError(e)),
  });

  const desconectar = useMutation({
    mutationFn: () => api(`/mercadopago/sucursales/${sucursalId}/cuenta`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Cuenta de Mercado Pago desconectada');
      setConfirmando(false);
      void qc.invalidateQueries({ queryKey: ['mp-cuenta', sucursalId] });
      void qc.invalidateQueries({ queryKey: ['mp-terminales', sucursalId] });
    },
    onError: (e) => {
      setConfirmando(false);
      if (e instanceof ErrorApi && e.code === 'TERMINAL_EN_USO') toast.error(mensajeParaCodigo('TERMINAL_EN_USO'));
      else toast.error(mensajeError(e));
    },
  });

  return (
    <ModuleLayout titulo="Configuración" secciones={SECCIONES_POR_MODULO.configuracion}>
      <EncabezadoPagina
        titulo="Mercado Pago"
        descripcion="Conecta la cuenta de Mercado Pago de la sucursal y vincula las terminales Point para cobrar con tarjeta."
      />
      <div className="flex max-w-3xl flex-col gap-5">
        {permitidas.length > 1 && (
          <div className="flex items-center gap-2">
            <label htmlFor="mp-sucursal" className="text-[13px] font-medium">
              Sucursal
            </label>
            <Select id="mp-sucursal" value={sucursalId ?? ''} onChange={(e) => setElegida(e.target.value)} className="w-full sm:w-64">
              {permitidas.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </Select>
          </div>
        )}

        {cuenta.isLoading || !sucursalId ? (
          <Esqueleto className="h-32" />
        ) : noConfigurado ? (
          <Tarjeta>
            <Vacio
              icono={<CreditCard />}
              titulo="Mercado Pago no está configurado en este servidor"
              descripcion="Pide a soporte que habilite la integración con Mercado Pago para poder conectar la cuenta."
            />
          </Tarjeta>
        ) : cuenta.isError ? (
          <Tarjeta>
            <Vacio icono={<CreditCard />} titulo="No se pudo cargar la conexión" descripcion={mensajeError(cuenta.error)} />
          </Tarjeta>
        ) : (
          <>
            <Tarjeta>
              <TarjetaEncabezado
                titulo="Cuenta"
                descripcion="Cada sucursal usa su propia cuenta de Mercado Pago."
                acciones={
                  conectada ? (
                    <Button variante="peligro" tamano="sm" onClick={() => setConfirmando(true)}>
                      <Unplug /> Desconectar
                    </Button>
                  ) : (
                    <Button tamano="sm" onClick={() => conectar.mutate()} cargando={conectar.isPending}>
                      Conectar Mercado Pago
                    </Button>
                  )
                }
              />
              <div className="px-4 py-4 text-[13.5px]">
                {conectada ? (
                  <dl className="grid gap-1 sm:grid-cols-[8rem_1fr]">
                    <dt className="text-foreground-muted">Estado</dt>
                    <dd>
                      <Insignia tono="exito">Conectada</Insignia>
                    </dd>
                    <dt className="text-foreground-muted">Cuenta</dt>
                    <dd className="break-all">{cuenta.data?.email}</dd>
                    {cuenta.data?.conectadaEn && (
                      <>
                        <dt className="text-foreground-muted">Conectada el</dt>
                        <dd>{fechaLarga(cuenta.data.conectadaEn)}</dd>
                      </>
                    )}
                    {cuenta.data?.conectadaPor && (
                      <>
                        <dt className="text-foreground-muted">Conectada por</dt>
                        <dd>{cuenta.data.conectadaPor}</dd>
                      </>
                    )}
                  </dl>
                ) : (
                  <p className="text-foreground-muted">
                    {cuenta.data?.estado === 'revocada'
                      ? 'La autorización fue revocada. Vuelve a conectar la cuenta para cobrar con tarjeta.'
                      : 'Esta sucursal aún no tiene una cuenta de Mercado Pago conectada.'}
                  </p>
                )}
              </div>
            </Tarjeta>

            {conectada && (
              <Tarjeta>
                <TarjetaEncabezado titulo="Terminales" descripcion="Vincula las terminales Point de esta cuenta y ponles un alias." />
                {terminales.isLoading ? (
                  <Esqueleto className="m-4 h-20" />
                ) : terminales.isError ? (
                  <Vacio titulo="No se pudieron cargar las terminales" descripcion={mensajeError(terminales.error)} />
                ) : terminales.data?.length ? (
                  <ul>
                    {terminales.data.map((t) => (
                      <FilaTerminal key={t.terminalId} t={t} sucursalId={sucursalId} />
                    ))}
                  </ul>
                ) : (
                  <Vacio icono={<CreditCard />} titulo="Sin terminales" descripcion="No hay terminales Point asociadas a esta cuenta." />
                )}
              </Tarjeta>
            )}
          </>
        )}
      </div>

      {confirmando && (
        <Dialogo
          abierto
          ancho="sm"
          onCambiarAbierto={(a) => !a && !desconectar.isPending && setConfirmando(false)}
          titulo="Desconectar Mercado Pago"
          descripcion="Se desactivarán las terminales y no se podrá cobrar con tarjeta en esta sucursal."
          pie={
            <>
              <Button variante="secundario" onClick={() => setConfirmando(false)} disabled={desconectar.isPending}>
                Cancelar
              </Button>
              <Button variante="peligro" onClick={() => desconectar.mutate()} cargando={desconectar.isPending}>
                Desconectar
              </Button>
            </>
          }
        >
          <p className="text-[13.5px]">Podrás volver a conectar la cuenta cuando quieras. No se puede desconectar si hay un cobro pendiente.</p>
        </Dialogo>
      )}
    </ModuleLayout>
  );
}
